import React, { createContext, useEffect, useState } from "react";
import io from "socket.io-client";
import { Error } from "../commons/Messages/Messages";

const SocketContext = createContext();

/**
 * The server only relays "refresh service provider data" within the sender's company, so the
 * socket has to say who it is ("company:join" with the JWT). The socket connects before login and
 * the token is renewed during a session, so it (re)joins lazily: on connect, and whenever it is
 * used with a token it has not joined with yet.
 */
const withCompanyJoin = socket => {
  let joinedWith = null;
  const emit = socket.emit.bind(socket);
  const on = socket.on.bind(socket);
  const ensureJoined = () => {
    const token = localStorage.getItem("token");
    if (!token || token === joinedWith || !socket.connected) return;
    joinedWith = token;
    socket.auth = { token };
    emit("company:join", { token });
  };
  socket.emit = (event, ...args) => {
    if (event !== "company:join") ensureJoined();
    return emit(event, ...args);
  };
  socket.on = (event, ...args) => {
    if (event !== "connect") ensureJoined();
    return on(event, ...args);
  };
  on("connect", () => {
    joinedWith = null; // rooms are lost on reconnect
    ensureJoined();
  });
  return socket;
};

const resolveSocketUrl = () => {
  let url = process.env.REACT_APP_SOCKET_URL;
  if (!url || url === "undefined" || url.includes("localhost:5001") || url.includes("127.0.0.1:5001")) {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL.replace(/\/api\/?$/, "");
    }
    return "http://localhost:5000";
  }
  return url;
};

const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const socketUrl = resolveSocketUrl();
    const newSocket = withCompanyJoin(
      io(socketUrl, {
        transports: ["websocket"],
        withCredentials: true,
        reconnectionAttempts: 5,
        autoConnect: true,
        auth: (cb) => {
          const token = localStorage.getItem("token");
          cb(token ? { token } : {});
        }
      })
    );

    // Chat / project / document actions the server refused (not allowed, not found, empty).
    newSocket.on("realtime:error", ({ errorMessage } = {}) => errorMessage && Error(errorMessage));

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export { SocketContext, SocketProvider };
